import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { api, apiUrl } from "@/lib/api";
import { t } from "@/lib/i18n";

/* Вход только через Google (как в zakup/market): бэкенд пускает почты, заведённые в «Настройках пользователей».
   Сессия — httpOnly cookie, здесь только читаем /api/auth/me. */

export type Role = "owner" | "admin" | "manager" | "agent" | "viewer";

export type HubUser = {
  id: string;
  email: string;
  name: string;
  role: Role;
  permissions: string[];
  avatarUrl?: string | null;
  can: { manageUsers: boolean; audit: boolean; manageFeedback: boolean; assignRoles: Role[]; financeAll: boolean; viewAs: boolean };
  /** владелец/администратор смотрит Hub глазами этого сотрудника (тестовый режим) */
  viewingAs?: { by: string; byName: string };
};

type AuthConfig = { google: boolean; devLogin: boolean };

type AuthContextValue = {
  user: HubUser | null;
  loading: boolean;
  config: AuthConfig | null;
  signInWithGoogle: () => void;
  devLogin: (email: string) => Promise<string | null>;
  signOut: () => Promise<void>;
  refresh: () => Promise<void>;
  /** посмотреть Hub глазами сотрудника / вернуться к себе — страница перезагружается, чтобы все разделы перечитали данные */
  viewAs: (userId: string) => Promise<void>;
  stopViewAs: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export const ROLE_LABELS: Record<Role, string> = { owner: t("Владелец"), admin: t("Администратор"), manager: t("Менеджер"), agent: t("Агент"), viewer: t("Наблюдатель") };

/** Ошибки возврата из Google (?error=…) — понятным текстом */
export const loginError = (code: string | null, email?: string | null) => {
  if (!code) return "";
  if (code === "not_invited")
    return t("Почта {email} не добавлена в Hub. Попросите владельца или администратора добавить её в «Настройках пользователей».", { email: email || "" }).replace("  ", " ");
  if (code === "access_denied") return t("Вход через Google отменён.");
  if (code === "session_expired") return t("Вы долго были неактивны — по настройкам пространства нужно войти снова.");
  if (code === "user_disabled") return t("Доступ отозван. Обратитесь к владельцу или администратору.");
  if (code === "state") return t("Сессия входа устарела — нажмите «Войти через Google» ещё раз.");
  return t("Не удалось войти через Google ({code}). Попробуйте ещё раз.", { code });
};

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<HubUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState<AuthConfig | null>(null);

  const refresh = useCallback(async () => {
    try {
      setUser(await api<HubUser>("/auth/me"));
    } catch {
      setUser(null);
    }
  }, []);

  useEffect(() => {
    let alive = true;
    Promise.all([refresh(), api<AuthConfig>("/auth/config").then((c) => alive && setConfig(c), () => undefined)]).finally(() => {
      if (alive) setLoading(false);
    });
    return () => {
      alive = false;
    };
  }, [refresh]);

  // любой запрос получил 401 (тайм-аут неактивности, отзыв доступа) — показываем экран входа с причиной
  useEffect(() => {
    const onUnauthorized = (e: Event) => {
      const code = String((e as CustomEvent).detail || "");
      if (code === "session_expired" || code === "user_disabled") {
        const url = new URL(window.location.href);
        url.searchParams.set("error", code);
        window.history.replaceState(null, "", url);
      }
      setUser(null);
    };
    window.addEventListener("hub:unauthorized", onUnauthorized);
    return () => window.removeEventListener("hub:unauthorized", onUnauthorized);
  }, []);

  const signInWithGoogle = useCallback(() => {
    window.location.href = apiUrl("/auth/google");
  }, []);

  const devLogin = useCallback(async (email: string) => {
    try {
      setUser(await api<HubUser>("/auth/dev-login", { method: "POST", json: { email } }));
      return null;
    } catch {
      return loginError("not_invited", email);
    }
  }, []);

  const signOut = useCallback(async () => {
    try {
      await api("/auth/logout", { method: "POST" });
    } catch {
      /* состояние всё равно сбрасываем */
    }
    setUser(null);
  }, []);

  const viewAs = useCallback(async (userId: string) => {
    await api("/auth/view-as", { method: "POST", json: { userId } });
    window.history.replaceState(null, "", "/");
    window.location.reload();
  }, []);

  const stopViewAs = useCallback(async () => {
    await api("/auth/view-as/stop", { method: "POST" });
    window.history.replaceState(null, "", "/");
    window.location.reload();
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ user, loading, config, signInWithGoogle, devLogin, signOut, refresh, viewAs, stopViewAs }),
    [user, loading, config, signInWithGoogle, devLogin, signOut, refresh, viewAs, stopViewAs],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
